import { Component, ViewChild, AfterViewInit, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, ExcelExportService, ToolbarService, FilterService, GridComponent, ExcelExportProperties } from '@syncfusion/ej2-angular-grids';
import { Workbook } from '@syncfusion/ej2-excel-export';
import { HospitalsAndExperiencesExcel } from '../../../../../../shared/interfaces/excel-hospitals-and-experiences';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DmtUtilityService } from '../../../../../../services/dashboard/dmt-utility.service';
import { HospitalTitlecasePipe } from '../../../../../../pipes/hospital-titlecase.pipe';

@Component({
  standalone: true,
  selector: 'app-generate-hospitals-and-experiences-excel',
  imports: [CommonModule, GridModule],
  providers: [ExcelExportService, ToolbarService, FilterService],
  templateUrl: './generate-hospitals-and-experiences-excel.component.html',
  styleUrl: './generate-hospitals-and-experiences-excel.component.css',
})
export class GenerateHospitalsAndExperiencesExcelComponent implements AfterViewInit, OnInit {

  @ViewChild('hospitalsGrid') grid!: GridComponent;
  @Output() sendEndMessage = new EventEmitter();

  hospitalsData: HospitalsAndExperiencesExcel[] = [];

  // flattened rows (region + hospital fields) used as the grid/export data source
  gridData: any[] = [];

  medicineId: number = 0;
  countryId: number = 0;

  private readonly hospitalTitlecasePipe = new HospitalTitlecasePipe();

  constructor (private dataService: DataService, private us: DmtUtilityService) { }

  ngOnInit(): void {
    this.medicineId = this.us.getSessionVariableNumber('medicineId', 0);
    this.countryId = this.us.getSessionVariableNumber('countryId');

    this.dataService.getExcelHospitalsAndExperiences(this.medicineId, this.countryId).subscribe((data: HospitalsAndExperiencesExcel[]) => {
      this.hospitalsData = data;
      this.gridData = this.flattenData(data);
    });
  }

  // flattens the region/hospitals structure into rows for the grid, grouped by region
  // (region row with its aggregated weight followed by its hospitals), heaviest first
  private flattenData (regions: HospitalsAndExperiencesExcel[]): any[] {
    const rows: any[] = [];

    const experienceTotal = regions.reduce(
      (regionsSum, region) =>
        regionsSum + region.hospitals.reduce((hospitalsSum, hospital) => hospitalsSum + hospital.potentialNumber, 0),
      0,
    );

    // order regions by number of experiences, heaviest first
    const sortedRegions = [...regions].sort((a, b) => {
      const totalA = a.hospitals.reduce((sum, hospital) => sum + hospital.potentialNumber, 0);
      const totalB = b.hospitals.reduce((sum, hospital) => sum + hospital.potentialNumber, 0);

      return totalB - totalA;
    });

    // empty spacer row before the first region
    rows.push({
      onekey: '',
      cluster: '',
      hospitalName: '',
      city: '',
      accountType: '',
      potentialNumber: '',
      italyPercentage: '',
      hospitalNote: '',
      isEmptyRow: true,
    });

    sortedRegions.forEach((region) => {
      const regionTotal = region.hospitals.reduce((sum, hospital) => sum + hospital.potentialNumber, 0);
      const regionPercentage = experienceTotal ? (regionTotal * 100) / experienceTotal : 0;

      // region summary row (region name shown under the onekey column)
      rows.push({
        onekey: this.toTitleCase(region.regionName),
        cluster: '',
        hospitalName: '',
        city: '',
        accountType: '',
        potentialNumber: regionTotal,
        italyPercentage: Number(regionPercentage.toFixed(2)), // numero vero, niente stringa/virgola/%
        hospitalNote: '',
        isRegion: true,
      });

      // hospitals header row
      rows.push({
        onekey: 'Ospedali ▾',
        cluster: '',
        hospitalName: '',
        city: '',
        accountType: '',
        potentialNumber: '',
        italyPercentage: '',
        hospitalNote: '',
        isHospitalTitle: true,
      });

      // hospitals of this region, heaviest first
      const sortedHospitals = [...region.hospitals].sort((a, b) => b.potentialNumber - a.potentialNumber);

      sortedHospitals.forEach((hospital) => {
        const italyPercentage = experienceTotal
          ? (hospital.potentialNumber * 100) / experienceTotal
          : 0;

        rows.push({
          onekey: hospital.oneKey,
          cluster: hospital.cluster,
          hospitalName: this.hospitalTitlecasePipe.transform(hospital.name),
          city: this.toTitleCase(hospital.city),
          accountType: this.formatAccountType(hospital.accountType),
          potentialNumber: hospital.potentialNumber,
          italyPercentage: Number(italyPercentage.toFixed(2)), // numero vero, niente stringa/virgola/%
          hospitalNote: hospital.hospitalNote,
          isHospital: true,
        });
      });

      // empty separator row between regions
      rows.push({
        onekey: '',
        cluster: '',
        hospitalName: '',
        city: '',
        accountType: '',
        potentialNumber: '',
        italyPercentage: '',
        hospitalNote: '',
        isEmptyRow: true,
      });
    });

    return rows;
  }

  // highlights region summary rows in the exported excel
  public onExcelQueryCellInfo (args: any): void {
    if (args.data && args.data.isRegion) {
      args.style = {
        ...args.style,
        backColor: '#fffacd',
        bold: true,
      };
    }
  }

  private toTitleCase (value: string | null | undefined): string {
    if (!value) return '';

    return value
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private formatAccountType (accountType: string): string {
    switch (accountType) {
      case 'Centro_Pubblico':
        return 'Centro Pubblico';
      case 'Centro_Privato':
        return 'Centro Privato';
      case 'Centro_Prescrittore':
      case 'Centro':
        return 'Centro Prescrittore';
      case 'PTwb':
        return 'Prescrittore con PTwb';
      case 'noPTwb':
        return 'Prescrittore senza PTwb';
      case 'Non_Identificato':
        return 'Non Identificato';
      default:
        return accountType;
    }
  }

  // waits for view init then starts export
  ngAfterViewInit (): void {
    setTimeout(() => this.exportGrid(), 1500);
  }

  // exports the grid to an excel file
  async exportGrid (): Promise<void> {
    const exportSettings: ExcelExportProperties = {
      fileName: 'Report_Ospedali_e_Esperienze.xlsx',
    };

    const workbook: any = await this.grid.excelExport(exportSettings, true);

    if (workbook && workbook.worksheets[0]) {
      workbook.worksheets[0].name = 'Ospedali';
    }

    if (workbook) {
      const book = new Workbook(workbook, 'xlsx');
      book.save('Report_Ospedali_e_Esperienze.xlsx');
      this.sendEndMessage.emit();
    }
  }
}